import QuillImport from 'quill';
import * as Emoji from 'quill-emoji';
import { forwardRef, useEffect, useLayoutEffect, useRef } from 'react';

import 'quill-emoji/dist/quill-emoji.css';
import 'quill/dist/quill.snow.css';
import styles from './quillEditor.module.css';

const QuillClass =
  (QuillImport as { default?: typeof QuillImport }).default ?? QuillImport;
QuillClass.register('modules/emoji', Emoji);

const MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    [{ size: ['small', false, 'large'] }],
    ['emoji'],
  ],
  'emoji-toolbar': true,
};

export interface QuillEditorProps {
  defaultValue?: string;
  onChange?(html: string): void;
  value?: string;
}

/**
 * Modeled off of Quill's [React playground example](https://quilljs.com/playground/react).
 *
 * The onChange handler is modeled after [react-quill's](https://github.com/zenoamaro/react-quill)
 * implementation, which calls the callback with the generated HTML string rather than the Delta objects.
 */
export const QuillEditor = forwardRef<HTMLDivElement, QuillEditorProps>(
  function QuillEditor({ defaultValue, onChange, value }, _ref) {
    const containerRef = useRef<HTMLDivElement>(null);
    const defaultValueRef = useRef(defaultValue ?? value ?? '');
    const onChangeRef = useRef(onChange);

    useLayoutEffect(() => {
      onChangeRef.current = onChange;
    });

    useEffect(() => {
      const container = containerRef.current;
      const editorContainer = container?.appendChild(
        container.ownerDocument.createElement('div')
      );
      if (!editorContainer) return;
      const quill = new QuillClass(editorContainer, {
        modules: MODULES,
        theme: 'snow',
      });

      if (defaultValueRef.current) {
        quill.setContents(quill.clipboard.convert(defaultValueRef.current));
      }

      quill.on('text-change', (_args) => {
        onChangeRef.current?.(quill.root.innerHTML);
      });

      return () => {
        if (container) {
          container.innerHTML = '';
        }
      };
    }, []);

    return <div ref={containerRef} className={styles['editor']}></div>;
  }
);
